import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import axios from "axios";
import { 
  Trophy, Swords, Crown, Edit2, Save, X, ArrowLeft,
  TrendingUp, TrendingDown, Minus, Heart, Users, Bookmark, Play, Film,
  Camera, Trash2, Mic, Flame, Info
} from "lucide-react";
import Navbar from "@/components/Navbar";
import MatchupCard from "@/components/MatchupCard";
import DebatePlayback from "@/components/DebatePlayback";

// Badge Component
const UserBadge = ({ badge, size = "sm" }) => {
  if (!badge) return null;
  const badgeStyles = {
    pro: "bg-gradient-to-r from-amber-500 to-orange-500 text-white",
    access: "bg-gradient-to-r from-blue-500 to-cyan-500 text-white",
    admin: "bg-gradient-to-r from-purple-500 to-pink-500 text-white"
  };
  const sizeStyles = {
    sm: "text-[10px] px-1.5 py-0.5",
    md: "text-xs px-2 py-1"
  };
  return (
    <span className={`rounded-full uppercase font-bold ${badgeStyles[badge] || ''} ${sizeStyles[size]}`}>
      {badge}
    </span>
  );
};

const ProfilePage = () => {
  const { userId } = useParams();
  const { user: currentUser, token, setUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [debates, setDebates] = useState([]);
  const [completedDebates, setCompletedDebates] = useState([]);
  const [highlights, setHighlights] = useState([]);
  const [supporters, setSupporters] = useState({ supporters: [], total: 0 });
  const [supporting, setSupporting] = useState({ supporting: [], total: 0 });
  const [isSupporting, setIsSupporting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [activeTab, setActiveTab] = useState("debates");
  const [selectedPlayback, setSelectedPlayback] = useState(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  
  const fileInputRef = useRef(null);
  
  const [editUsername, setEditUsername] = useState("");
  const [editBio, setEditBio] = useState("");
  const [editStageColor, setEditStageColor] = useState("");

  const isOwnProfile = currentUser?.user_id === userId;

  // Handle profile picture upload
  const handlePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }
    
    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }
    
    setUploadingPicture(true);
    const formData = new FormData();
    formData.append('picture', file);
    
    try {
      const res = await axios.post(`${API}/users/profile-picture`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Update local state
      setProfile(prev => ({ ...prev, picture: res.data.picture_url }));
      setUser(prev => ({ ...prev, picture: res.data.picture_url }));
      toast.success("Profile picture updated!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to upload picture");
    } finally {
      setUploadingPicture(false);
    }
  };
  
  // Handle profile picture removal
  const handleRemovePicture = async () => {
    if (!window.confirm("Remove your profile picture?")) return;
    
    try {
      await axios.delete(`${API}/users/profile-picture`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setProfile(prev => ({ ...prev, picture: null }));
      setUser(prev => ({ ...prev, picture: null }));
      toast.success("Profile picture removed");
    } catch (err) {
      toast.error("Failed to remove picture");
    }
  };

  useEffect(() => {
    fetchProfile();
  }, [userId]);

  const fetchProfile = async () => {
    try {
      const [profileRes, debatesRes, highlightsRes, supportersRes, playbackRes] = await Promise.all([
        axios.get(`${API}/users/${userId}`),
        axios.get(`${API}/users/${userId}/debates`),
        axios.get(`${API}/highlights/user/${userId}`),
        axios.get(`${API}/users/${userId}/supporters`),
        axios.get(`${API}/users/${userId}/debates/playback`).catch(() => ({ data: { completed_debates: [] } }))
      ]);
      setProfile(profileRes.data);
      setDebates(debatesRes.data);
      setHighlights(highlightsRes.data.highlights || []);
      setSupporters(supportersRes.data);
      setCompletedDebates(playbackRes.data.completed_debates || []);
      setEditUsername(profileRes.data.username);
      setEditBio(profileRes.data.bio || "");
      setEditStageColor(profileRes.data.stage_color || "red");
      
      // Check if current user is supporting this profile
      if (token && currentUser && currentUser.user_id !== userId) {
        try {
          const statusRes = await axios.get(`${API}/support/${userId}/status`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setIsSupporting(statusRes.data.is_supporting);
        } catch {
          // Not supporting yet, expected
        }
      }
      
      // Get who this user is supporting
      try {
        const supportingRes = await axios.get(`${API}/users/${userId}/supporting`);
        setSupporting(supportingRes.data);
      } catch {
        // Failed to get supporting list
      }
    } catch (e) {
      toast.error("User not found");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      const res = await axios.put(`${API}/users/profile`, {
        username: editUsername,
        bio: editBio,
        stage_color: editStageColor
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(res.data);
      setUser(res.data);
      setEditing(false);
      toast.success("Profile updated!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update profile");
    }
  };

  const handleSupport = async () => {
    if (!token) {
      toast.error("Please log in to support");
      return;
    }
    
    try {
      if (isSupporting) {
        await axios.delete(`${API}/support/${userId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsSupporting(false);
        setSupporters(prev => ({ ...prev, total: prev.total - 1 }));
        toast.success("Support removed");
      } else {
        await axios.post(`${API}/support`, { target_user_id: userId }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsSupporting(true);
        setSupporters(prev => ({ ...prev, total: prev.total + 1 }));
        toast.success(`Now supporting ${profile.username}!`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update support");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] relative flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!profile) return null;

  const winRate = profile.total_debates > 0 
    ? ((profile.wins / profile.total_debates) * 100).toFixed(1) 
    : 0;

  const stageColors = [
    { value: "red", label: "Red", class: "bg-[#e11d48]" },
    { value: "blue", label: "Blue", class: "bg-[#3b82f6]" },
    { value: "purple", label: "Purple", class: "bg-purple-500" },
    { value: "green", label: "Green", class: "bg-green-500" },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0f] relative">
      {/* Ambient red/blue glow */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-[20%] -left-[20%] w-[70%] h-[50%] rounded-full bg-red-600/20 blur-[100px]" />
        <div className="absolute -bottom-[20%] -right-[20%] w-[70%] h-[50%] rounded-full bg-blue-600/25 blur-[100px]" />
      </div>
      <Navbar />
      
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-8">
        <Link 
          to="/browse" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 group text-sm"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          Back to Debates
        </Link>

        {/* Profile Header */}
        <div className="bg-card/30 rounded-lg border border-white/10 p-6 md:p-8 mb-6">
          <div className="flex flex-col md:flex-row items-start gap-6">
            {/* Avatar with Stage Glow and Upload */}
            <div className={`relative p-2 rounded-full stage-glow-${profile.stage_color || 'red'}`}>
              <Avatar className="w-28 h-28 md:w-32 md:h-32 border-4 border-white/20">
                <AvatarImage src={profile.picture} />
                <AvatarFallback className={`text-4xl ${
                  profile.stage_color === 'blue' ? 'bg-[#3b82f6]' :
                  profile.stage_color === 'purple' ? 'bg-purple-500' :
                  profile.stage_color === 'green' ? 'bg-green-500' :
                  'bg-[#e11d48]'
                }`}>
                  {profile.username[0].toUpperCase()}
                </AvatarFallback>
              </Avatar>
              
              {/* Profile Picture Upload Controls - Only show on own profile */}
              {isOwnProfile && (
                <div className="absolute -bottom-1 -right-1 flex gap-1">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handlePictureUpload}
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingPicture}
                    className="p-2 bg-primary rounded-full hover:bg-primary/90 transition-colors shadow-lg"
                    title="Upload photo"
                  >
                    {uploadingPicture ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Camera className="w-4 h-4 text-white" />
                    )}
                  </button>
                  {profile.picture && (
                    <button
                      onClick={handleRemovePicture}
                      className="p-2 bg-red-500 rounded-full hover:bg-red-600 transition-colors shadow-lg"
                      title="Remove photo"
                    >
                      <Trash2 className="w-4 h-4 text-white" />
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Info */}
            <div className="flex-1">
              {editing ? (
                <div className="space-y-4">
                  <div>
                    <Label>Username</Label>
                    <Input
                      data-testid="edit-username"
                      value={editUsername}
                      onChange={(e) => setEditUsername(e.target.value)}
                      className="mt-1 bg-zinc-900/50 border-white/10"
                    />
                  </div>
                  <div>
                    <Label>Bio</Label>
                    <Textarea
                      data-testid="edit-bio"
                      value={editBio}
                      onChange={(e) => setEditBio(e.target.value)}
                      placeholder="Tell us about yourself..."
                      className="mt-1 bg-zinc-900/50 border-white/10 resize-none"
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label>Stage Color</Label>
                    <div className="flex gap-3 mt-2">
                      {stageColors.map(color => (
                        <button
                          key={color.value}
                          type="button"
                          data-testid={`color-${color.value}`}
                          onClick={() => setEditStageColor(color.value)}
                          className={`w-10 h-10 rounded-full ${color.class} ${
                            editStageColor === color.value ? 'ring-2 ring-white ring-offset-2 ring-offset-background' : ''
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Button 
                      data-testid="save-profile"
                      onClick={handleSaveProfile}
                      className="bg-primary text-primary-foreground"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Save
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => setEditing(false)}
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex flex-wrap items-center gap-3 mb-3">
                    <h1 className="text-2xl md:text-3xl font-bold">{profile.username}</h1>
                    <UserBadge badge={profile.badge} size="md" />
                    {profile.wins >= 10 && (
                      <Badge className="bg-accent text-accent-foreground">
                        <Crown className="w-3 h-3 mr-1" />
                        Top Debater
                      </Badge>
                    )}
                  </div>
                  <p className="text-muted-foreground mb-3">
                    {profile.bio || "No bio yet"}
                  </p>
                  
                  {/* Supporters Count */}
                  <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                    <span className="flex items-center gap-1">
                      <Heart className="w-4 h-4 text-pink-500" />
                      <strong className="text-foreground">{supporters.total}</strong> Supporters
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      <strong className="text-foreground">{supporting.total}</strong> Supporting
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <p className="text-xs text-muted-foreground">
                      Member since {new Date(profile.created_at).toLocaleDateString()}
                    </p>
                    
                    {isOwnProfile ? (
                      <Button 
                        data-testid="edit-profile-btn"
                        variant="outline" 
                        size="sm"
                        onClick={() => setEditing(true)}
                      >
                        <Edit2 className="w-4 h-4 mr-2" />
                        Edit Profile
                      </Button>
                    ) : currentUser && (
                      <Button 
                        variant={isSupporting ? "secondary" : "default"}
                        size="sm"
                        onClick={handleSupport}
                        className={isSupporting ? "" : "bg-pink-600 hover:bg-pink-700"}
                      >
                        <Heart className={`w-4 h-4 mr-2 ${isSupporting ? 'fill-current' : ''}`} />
                        {isSupporting ? "Supporting" : "Support"}
                      </Button>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 w-full md:w-auto">
              <div className="bg-zinc-900/50 border border-white/5 p-3 md:p-4 rounded-md text-center">
                <Trophy className="w-5 h-5 text-accent mx-auto mb-1" />
                <p className="text-xl md:text-2xl font-bold mono">{profile.wins}</p>
                <p className="text-[10px] md:text-xs text-muted-foreground uppercase">Wins</p>
              </div>
              <div className="bg-zinc-900/50 border border-white/5 p-3 md:p-4 rounded-md text-center">
                <Swords className="w-5 h-5 text-muted-foreground mx-auto mb-1" />
                <p className="text-xl md:text-2xl font-bold mono">{profile.total_debates}</p>
                <p className="text-[10px] md:text-xs text-muted-foreground uppercase">Debates</p>
              </div>
              <div className="bg-zinc-900/50 border border-white/5 p-3 md:p-4 rounded-md text-center">
                {winRate >= 60 ? <TrendingUp className="w-5 h-5 text-green-500 mx-auto mb-1" /> :
                 winRate <= 40 && profile.total_debates > 0 ? <TrendingDown className="w-5 h-5 text-red-500 mx-auto mb-1" /> :
                 <Minus className="w-5 h-5 text-muted-foreground mx-auto mb-1" />}
                <p className="text-xl md:text-2xl font-bold mono">{winRate}%</p>
                <p className="text-[10px] md:text-xs text-muted-foreground uppercase">Win Rate</p>
              </div>
              {/* Spar Rating */}
              <div className="bg-zinc-900/50 border border-white/5 p-3 md:p-4 rounded-md text-center relative">
                <Flame className={`w-5 h-5 mx-auto mb-1 ${
                  profile.average_spar_rating >= 80 ? 'text-green-400' :
                  profile.average_spar_rating >= 60 ? 'text-yellow-400' :
                  profile.average_spar_rating > 0 ? 'text-orange-400' :
                  'text-muted-foreground'
                }`} />
                <p className={`text-xl md:text-2xl font-bold mono ${
                  profile.average_spar_rating >= 80 ? 'text-green-400' :
                  profile.average_spar_rating >= 60 ? 'text-yellow-400' :
                  profile.average_spar_rating > 0 ? 'text-orange-400' :
                  'text-muted-foreground'
                }`}>
                  {profile.average_spar_rating ? profile.average_spar_rating.toFixed(0) : '—'}
                </p>
                <div className="flex items-center justify-center gap-1">
                  <p className="text-[10px] md:text-xs text-muted-foreground uppercase">Spar Rating</p>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button className="text-muted-foreground hover:text-foreground">
                          <Info className="w-3 h-3" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs bg-zinc-800 text-white border-white/10">
                        <p className="text-sm">Spar Rating measures performance — presence, energy, clarity, and pacing.</p>
                        <p className="text-sm mt-1">It does not decide the winner. The crowd vote does.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-white/10 overflow-x-auto">
          {[
            { id: "debates", label: "Debates", count: debates.length },
            { id: "replays", label: "Replays", count: completedDebates.length },
            { id: "highlights", label: "Highlight Reel", count: highlights.length },
            { id: "supporters", label: "Supporters", count: supporters.total }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px ${
                activeTab === tab.id 
                  ? 'border-primary text-foreground' 
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
              <span className="ml-2 text-xs bg-white/10 px-1.5 py-0.5 rounded">{tab.count}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === "debates" && (
          <div>
            {debates.length === 0 ? (
              <div className="text-center py-12 bg-card/30 rounded-lg border border-white/10">
                <Swords className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground text-sm">No debates yet</p>
                {isOwnProfile && (
                  <Link to="/create">
                    <Button className="mt-4" size="sm">Start Your First Debate</Button>
                  </Link>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {debates.map(debate => (
                  <MatchupCard key={debate.debate_id} debate={debate} />
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "replays" && (
          <div>
            {selectedPlayback ? (
              <div className="bg-card/30 rounded-lg border border-white/10 p-4">
                <DebatePlayback 
                  debateId={selectedPlayback} 
                  onClose={() => setSelectedPlayback(null)} 
                />
              </div>
            ) : completedDebates.length === 0 ? (
              <div className="text-center py-12 bg-card/30 rounded-lg border border-white/10">
                <Film className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground text-sm">No completed debates yet</p>
                <p className="text-xs text-muted-foreground mt-2">
                  Replays will appear here after completing all rounds of a debate
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {completedDebates.map(debate => (
                  <button
                    key={debate.debate_id}
                    onClick={() => setSelectedPlayback(debate.debate_id)}
                    className="bg-card/30 border border-white/10 rounded-lg p-4 hover:border-primary/50 transition-colors text-left group"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant="outline" className={debate.user_side === "red" ? "text-red-400 border-red-400/30" : "text-blue-400 border-blue-400/30"}>
                        {debate.user_side === "red" ? "🔴 Red Corner" : "🔵 Blue Corner"}
                      </Badge>
                      <Badge variant="outline" className="text-green-400 border-green-400/30">
                        <Film className="w-3 h-3 mr-1" />
                        {debate.clips_count} clips
                      </Badge>
                    </div>
                    <h4 className="font-medium truncate group-hover:text-primary transition-colors">
                      {debate.title}
                    </h4>
                    <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                      <span>vs</span>
                      <Avatar className="w-5 h-5">
                        <AvatarImage src={debate.opponent_picture} />
                        <AvatarFallback className="text-[8px]">{debate.opponent_username?.[0]}</AvatarFallback>
                      </Avatar>
                      <span className="truncate">{debate.opponent_username}</span>
                    </div>
                    <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between text-xs text-muted-foreground">
                      <span>{debate.category}</span>
                      <span className="flex items-center gap-1 text-primary group-hover:underline">
                        <Play className="w-3 h-3" />
                        Watch Replay
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "highlights" && (
          <div>
            {highlights.length === 0 ? (
              <div className="text-center py-12 bg-card/30 rounded-lg border border-white/10">
                <Bookmark className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground text-sm">No highlights yet</p>
                {isOwnProfile && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Mark moments during your debates to add to your reel
                  </p>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {highlights.map(highlight => (
                  <Link 
                    key={highlight.highlight_id}
                    to={`/debate/${highlight.debate_id}`}
                    className="bg-card/30 border border-white/10 rounded-lg p-4 hover:border-white/20 transition-colors group"
                  >
                    <div className="aspect-video bg-zinc-900 rounded-md mb-3 flex items-center justify-center relative overflow-hidden">
                      <Play className="w-10 h-10 text-muted-foreground group-hover:text-white transition-colors" />
                      <div className="absolute bottom-2 right-2 bg-black/80 text-xs px-2 py-1 rounded">
                        {highlight.end_time - highlight.start_time}s
                      </div>
                    </div>
                    <h4 className="font-medium text-sm truncate">
                      {highlight.title || `Highlight from ${highlight.debate_title}`}
                    </h4>
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(highlight.created_at).toLocaleDateString()}
                    </p>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "supporters" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {supporters.supporters?.length === 0 ? (
              <div className="col-span-full text-center py-12 bg-card/30 rounded-lg border border-white/10">
                <Heart className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground text-sm">No supporters yet</p>
              </div>
            ) : (
              supporters.supporters?.map(supporter => (
                <Link 
                  key={supporter.user_id}
                  to={`/profile/${supporter.user_id}`}
                  className="flex items-center gap-3 bg-card/30 border border-white/10 rounded-lg p-4 hover:border-white/20 transition-colors"
                >
                  <Avatar className="w-12 h-12">
                    <AvatarImage src={supporter.picture} />
                    <AvatarFallback>{supporter.username[0].toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">{supporter.username}</span>
                      <UserBadge badge={supporter.badge} />
                    </div>
                  </div>
                  <Heart className="w-4 h-4 text-pink-500 fill-pink-500" />
                </Link>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
